import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  InternalHookFormCheckbox,
  InternalHookFormDateTimePicker,
  InternalHookFormInput,
} from '../../../atoms';
import { InternalSelect } from '../../../atoms/internal-select/internal-select';
import { CallOutcomeFormFields } from '../../../../types';
import { KeyValuePair } from '../../../../types/common.types';
import { callOutcomeFormValidationSchema } from '../../../../validators';

const INPUT_CLASSES =
  'w-full rounded-md border-gray-300 focus:border-secondary px-3 py-1.5 text-sm outline-none';

const READ_ONLY_CLASSES = 'rounded-md bg-grey-brand-1 px-3 py-1.5 text-sm text-text-grey';

const formatDuration = (totalSeconds: number) => {
  const seconds = Number.isFinite(+totalSeconds) ? Math.max(0, Math.floor(+totalSeconds)) : 0;
  const minutes = Math.floor(seconds / 60);

  return `${`${minutes}`.padStart(2, '0')}:${`${seconds % 60}`.padStart(2, '0')}`;
};

type FieldProps = {
  label: string;
  children: React.ReactNode;
};

const Field: React.FC<FieldProps> = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xxs font-semibold uppercase tracking-wider text-text-grey">{label}</span>
    {children}
  </label>
);

type CallOutcomeFormProps = {
  defaultValues: CallOutcomeFormFields;
  onSubmit: (values: CallOutcomeFormFields) => void;
  callOutcomeSelectValues: KeyValuePair<string>[];
  isSubmitLoading: boolean;
};

export const CallOutcomeForm: React.FC<CallOutcomeFormProps> = ({
  defaultValues,
  onSubmit,
  callOutcomeSelectValues,
  isSubmitLoading,
}) => {
  const {
    handleSubmit,
    register,
    control,
    getValues,
    reset,
    setValue,
    watch,
    formState: { errors, dirtyFields, touchedFields },
  } = useForm({
    defaultValues,
    resolver: yupResolver(callOutcomeFormValidationSchema),
    mode: 'onTouched',
  });

  const durationInSeconds = watch('durationInSeconds');

  const submit = async () => {
    await onSubmit(getValues() as CallOutcomeFormFields);
    reset({}, { keepValues: false });
  };

  useEffect(() => {
    let durationCounter = 0;
    const interval = setInterval(() => {
      setValue('durationInSeconds', durationCounter);

      durationCounter += 1;
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <form className="flex flex-col flex-grow gap-3 p-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-row items-center justify-between gap-3 rounded-md bg-grey-brand-1 px-3 py-2">
        <div className="flex flex-col">
          <span className="text-xxs font-semibold uppercase tracking-wider text-text-grey">
            Call Duration
          </span>
          <span className="text-lg font-bold leading-tight text-secondary">
            {formatDuration(durationInSeconds)}
          </span>
        </div>
        <div className="flex flex-col text-right">
          <span className="text-xxs font-semibold uppercase tracking-wider text-text-grey">
            Agent
          </span>
          <span className="text-sm">{getValues('agentFullName')}</span>
        </div>
      </div>
      <InternalHookFormInput variableName="durationInSeconds" register={register} hidden />
      <InternalHookFormInput variableName="agentFullName" register={register} hidden />
      <Field label="Call Date & Time">
        <div className={READ_ONLY_CLASSES}>{getValues('callDate')}</div>
        <InternalHookFormInput variableName="callDate" register={register} hidden />
      </Field>
      <Field label="Call Outcome">
        <InternalSelect
          variableName="callOutcome"
          options={callOutcomeSelectValues.map((item) => ({ value: item.value, key: item.key }))}
          register={register}
          className={`${INPUT_CLASSES} border-2 border-solid bg-white`}
        />
      </Field>
      {dirtyFields.callBack ? (
        <Field label="Call-back Date">
          <InternalHookFormDateTimePicker
            control={control}
            variableName="callBackDate"
            touchedFields={touchedFields}
            errors={errors}
            classNameOverride={`${INPUT_CLASSES} small-datetime-picker`}
            businessHoursOnly
          />
        </Field>
      ) : (
        <></>
      )}
      <div className="mt-auto -mx-4 flex flex-wrap items-center justify-between gap-3 border-t border-grey-brand-1 px-4 pt-3">
        <span className="flex flex-row items-center gap-4">
          <span className="flex items-center gap-1">
            <InternalHookFormCheckbox control={control} variableName="doNotContact" />
            <span className="text-xs">Do not contact</span>
          </span>
          <span className="flex items-center gap-1">
            <InternalHookFormCheckbox control={control} variableName="callBack" />
            <span className="text-xs">Call back</span>
          </span>
        </span>
        <Button
          isLoading={isSubmitLoading}
          onClick={handleSubmit(submit)}
          className="bg-primary hover:bg-primary-dark text-white text-xs font-semibold px-4 rounded-md"
          spinnerSize={10.5}
        >
          Complete Call
        </Button>
      </div>
    </form>
  );
};
